import { Injectable } from '@angular/core';

/**
 * Interface for error fingerprint data
 */
interface ErrorFingerprint {
  hash: string;
  firstSeen: number;
  lastSeen: number;
  count: number;
  message: string;
}

/**
 * Configuration for error deduplication
 */
interface DeduplicationConfig {
  /** Maximum number of errors to keep in memory (LRU cache) */
  maxCacheSize: number;
  /** Time window in milliseconds for throttling same errors */
  throttleWindowMs: number;
  /** Maximum number of same error allowed per throttle window */
  maxErrorsPerWindow: number;
  /** Critical error bypass - always log these regardless of throttling */
  criticalErrorBypass: boolean;
}

/**
 * Service for deduplicating errors to prevent spam logging
 * Uses error fingerprinting and time-based throttling
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorDeduplicationService {
  private readonly errorCache = new Map<string, ErrorFingerprint>();
  private readonly config: DeduplicationConfig;

  constructor() {
    // Default configuration - can be made configurable per environment
    this.config = {
      maxCacheSize: 50,
      throttleWindowMs: 30000, // 30 seconds
      maxErrorsPerWindow: 1,
      criticalErrorBypass: true
    };
  }

  /**
   * Checks if an error should be logged based on deduplication rules
   * @param error The error to check
   * @param source Source of the error (e.g., 'GlobalErrorHandler')
   * @returns true if error should be logged, false if it should be throttled
   */
  shouldLogError(error: any, source?: string): boolean {
    const fingerprint = this.generateErrorFingerprint(error, source);
    const now = Date.now();

    // Check if this is a critical error that should bypass throttling
    if (this.config.criticalErrorBypass && this.isCriticalError(error)) {
      this.updateErrorCache(fingerprint, now);
      return true;
    }

    const existingError = this.errorCache.get(fingerprint);

    if (!existingError) {
      // New error - log it and add to cache
      this.updateErrorCache(fingerprint, now);
      this.cleanupOldEntries();
      return true;
    }

    // Check if error is within throttle window
    const timeSinceLastLog = now - existingError.lastSeen;
    if (timeSinceLastLog < this.config.throttleWindowMs) {
      // Within throttle window - check count
      if (existingError.count >= this.config.maxErrorsPerWindow) {
        // Throttle this error
        existingError.count++;
        existingError.lastSeen = now;
        return false;
      }
    }

    // Log this error and update cache
    this.updateErrorCache(fingerprint, now);
    return true;
  }

  /**
   * Gets statistics about throttled errors
   * @returns Object with throttling statistics
   */
  getThrottlingStats(): { totalErrors: number; throttledErrors: number; uniqueErrors: number } {
    let totalErrors = 0;
    let throttledErrors = 0;

    for (const error of this.errorCache.values()) {
      totalErrors += error.count;
      if (error.count > this.config.maxErrorsPerWindow) {
        throttledErrors += (error.count - this.config.maxErrorsPerWindow);
      }
    }

    return {
      totalErrors,
      throttledErrors,
      uniqueErrors: this.errorCache.size
    };
  }

  /**
   * Clears the error cache (useful for testing or manual reset)
   */
  clearCache(): void {
    this.errorCache.clear();
  }

  /**
   * Generates a unique fingerprint for an error
   * @param error The error object
   * @param source Source of the error
   * @returns Unique hash string for the error
   */
  private generateErrorFingerprint(error: any, source?: string): string {
    const message = this.extractErrorMessage(error);
    const stack = this.extractErrorStack(error);
    
    // Create a simple hash from message + key parts of stack + source
    const stackTopLines = stack ? stack.split('\n').slice(0, 3).join('') : '';
    const hashInput = `${source || 'unknown'}:${message}:${stackTopLines}`;
    
    return this.simpleHash(hashInput);
  }

  /**
   * Creates a simple hash from a string (not cryptographically secure)
   * @param str String to hash
   * @returns Simple hash string
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  /**
   * Extracts error message from various error formats
   * @param error Error object
   * @returns Error message string
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.originalError?.message) return error.originalError.message;
    if (error?.rejection?.message) return error.rejection.message;
    return 'Unknown error';
  }

  /**
   * Extracts error stack trace from various error formats
   * @param error Error object
   * @returns Stack trace string or null
   */
  private extractErrorStack(error: any): string | null {
    if (error?.stack) return error.stack;
    if (error?.originalError?.stack) return error.originalError.stack;
    if (error?.rejection?.stack) return error.rejection.stack;
    return null;
  }

  /**
   * Determines if an error should be considered critical and bypass throttling
   * @param error Error object
   * @returns true if error is critical
   */
  private isCriticalError(error: any): boolean {
    const message = this.extractErrorMessage(error).toLowerCase();
    
    // Define critical error patterns
    const criticalPatterns = [
      'security',
      'unauthorized',
      'forbidden',
      'payment',
      'billing',
      'corruption',
      'data loss',
      'memory leak'
    ];

    return criticalPatterns.some(pattern => message.includes(pattern));
  }

  /**
   * Updates the error cache with new error occurrence
   * @param fingerprint Error fingerprint hash
   * @param timestamp Current timestamp
   */
  private updateErrorCache(fingerprint: string, timestamp: number): void {
    const existing = this.errorCache.get(fingerprint);
    
    if (existing) {
      existing.lastSeen = timestamp;
      existing.count++;
    } else {
      // Extract message for debugging
      const message = 'Error logged'; // Could be enhanced to store actual message
      
      this.errorCache.set(fingerprint, {
        hash: fingerprint,
        firstSeen: timestamp,
        lastSeen: timestamp,
        count: 1,
        message
      });
    }
  }

  /**
   * Removes old entries from cache to prevent memory leaks
   * Uses LRU-style cleanup
   */
  private cleanupOldEntries(): void {
    if (this.errorCache.size <= this.config.maxCacheSize) {
      return;
    }

    // Convert to array and sort by lastSeen (oldest first)
    const entries = Array.from(this.errorCache.entries())
      .sort(([, a], [, b]) => a.lastSeen - b.lastSeen);

    // Remove oldest entries until we're under the limit
    const entriesToRemove = this.errorCache.size - this.config.maxCacheSize;
    for (let i = 0; i < entriesToRemove; i++) {
      this.errorCache.delete(entries[i][0]);
    }
  }
}